// Chat message list host. Renders messages through
// `cdk-virtual-scroll-viewport` with a custom virtual-scroll strategy
// (`MessageListScrollStrategy`) that maintains an explicit per-row height
// cache.
//
// Architectural notes:
//   - Full message array goes straight to `*cdkVirtualFor`; there is no
//     `_visibleCount` window. The strategy renders only what is currently in
//     viewport (plus a buffer), so DOM stays small regardless of the store
//     size. An earlier prototype used a growing window on top of CDK
//     virtualization, which mutated data length mid-scroll and caused
//     mid-scroll blank gaps and direction-flipping jumps.
//   - An earlier prototype used `@angular/cdk-experimental`'s autosize
//     strategy. It estimates total content size from sampled rows; the
//     estimate drifts as more rows render, which surfaced as scrollbar lag
//     during drag, initial scroll-to-latest landing short, and
//     re-anchor jumps when scrolling up. The custom strategy here works
//     from real measurements (ResizeObserver below feeds heights in) and
//     all three classes of bug go away.
//   - Row spacing is `padding-bottom` rather than `margin-bottom` so that
//     ResizeObserver's `borderBoxSize` captures the full per-row layout
//     height. Margins would sit outside the border-box and be invisible
//     to the observer.
//   - `scrollToMessage` / `scrollToFirstUnread` can now use
//     `viewport.scrollToIndex(index, 'smooth')` directly (the custom
//     strategy implements it properly), then a one-shot `scrollIntoView`
//     to centre the target.
import {
  afterNextRender,
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  HostListener,
  inject,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslatePipe } from '@ngx-translate/core';
import { CdkVirtualForOf, CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import {
  AndorMessageListScrollDirective,
  MessageListScrollStrategy,
} from './message-list-scroll-strategy';
import { faPaperPlane } from '@fortawesome/pro-duotone-svg-icons/faPaperPlane';
import { faImage } from '@fortawesome/pro-duotone-svg-icons/faImage';
import { faXmark } from '@fortawesome/pro-solid-svg-icons/faXmark';
import {
  faTriangleExclamation,
  faCircleInfo,
  faArrowsMinimize,
  faSpinnerThird,
} from '@fortawesome/pro-duotone-svg-icons';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { OdinService } from '../../../core/services/odin.service';
import {
  isUnread,
  MessageStore,
  Message,
  TextMessageWirePayload,
} from '../../../state/messages.repository';
import { MessageReadStateStore } from '../../../state/message-read-state.repository';
import { UserService } from '../../../core/services/user.service';
import { User, UserStore } from '../../../state/user.repository';
import { UserSettingsStore } from '../../../state/user-settings.repository';
import { ChatMsg } from '../chat-msg/chat-msg';
import { ContextMenu } from '../../../shared/components/ui/context-menu/context-menu';
import { EmojiPicker } from '../../../shared/components/form-fields/emoji-picker/emoji-picker';
import { sleep } from '../../../shared/helpers/async';
import { imageDimensions } from '../../../shared/helpers/image-dimensions';
import { estimateMessageHeight } from './message-height-estimator';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  fromEvent,
  map,
  merge,
  of,
  Subject,
  switchMap,
  takeUntil,
  timer,
} from 'rxjs';
import { RTE } from '../rte/rte';
import { BlobUrlPipe } from '../../../shared/pipes/blob-url.pipe';
import { ChatService } from '../../../core/services/chat.service';
import { faMessageSmile } from '@fortawesome/pro-duotone-svg-icons';
import { DevConsoleService } from '../../../core/services/dev-console.service';
import imageCompression from 'browser-image-compression';

@Component({
  selector: 'andor-chat',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FontAwesomeModule,
    TranslatePipe,
    ScrollingModule,
    CdkVirtualForOf,
    CdkVirtualScrollViewport,
    AndorMessageListScrollDirective,
    ChatMsg,
    ContextMenu,
    EmojiPicker,
    RTE,
    BlobUrlPipe,
  ],
  templateUrl: './chat.html',
  styleUrl: './chat.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Chat implements OnInit, AfterViewInit, OnDestroy {
  @Input() mode: 'pinned' | 'main' = 'main';
  protected readonly faPaperPlane = faPaperPlane;
  protected readonly faMessageSmile = faMessageSmile;
  protected readonly faImage = faImage;
  protected readonly faXmark = faXmark;
  protected readonly faTriangleExclamation = faTriangleExclamation;
  protected readonly faCircleInfo = faCircleInfo;
  protected readonly faArrowsMinimize = faArrowsMinimize;
  protected readonly faSpinnerThird = faSpinnerThird;

  static readonly MAX_TEXT_LENGTH = 10000;
  static readonly MAX_IMAGE_BYTES = 2.5 * 1024 * 1024;
  @ViewChild(CdkVirtualScrollViewport) viewport!: CdkVirtualScrollViewport;
  @ViewChild(AndorMessageListScrollDirective)
  scrollStrategyDir!: AndorMessageListScrollDirective;
  private get _strategy(): MessageListScrollStrategy | undefined {
    return this.scrollStrategyDir?.strategy;
  }
  @ViewChild('inputField') inputField!: RTE;
  @ViewChild('sendButton') sendButton!: ElementRef;
  @ViewChild('imagePopoverContainer') imagePopoverContainer!: ElementRef;
  @HostListener('document:keypress', ['$event'])
  async handleKeyboardEvent(event: KeyboardEvent) {
    if ((event.code === 'Enter' || event.code === 'NumpadEnter') && !event.shiftKey) {
      event.preventDefault();
      await this.sendMsg();
    }
  }
  private _allMessages = signal<Message[]>([]);
  // Whole message list — no windowing. CDK virtual scroll only renders rows
  // currently in viewport (plus buffer), so DOM stays small regardless of the
  // store's size.
  visibleMessages = this._allMessages.asReadonly();
  imagePreview = signal<string | undefined>(undefined);
  popoverImage = signal<string>('');
  errorMessage = signal<string | undefined>(undefined);
  private _errorTimer?: ReturnType<typeof setTimeout>;

  imageNeedsCompression = signal(false);
  isCompressing = signal(false);
  private _pendingBlob: Blob | null = null;

  private static readonly _AT_BOTTOM_TOLERANCE_PX = 60;
  private _destroy$ = new Subject<void>();
  private _atBottom$ = new BehaviorSubject<boolean>(true);
  private _tabVisible$ = new BehaviorSubject<boolean>(document.visibilityState === 'visible');
  private _injector = inject(Injector);
  // One ResizeObserver feeds row heights into the strategy. Observed
  // elements are tracked in a WeakSet so we don't re-observe the same row
  // across renders. CDK virtual-scroll recycles view templates rather than
  // detaching elements, so an element observed once is fine to leave
  // observed — its `entry.target.id` will reflect whatever data the
  // recycled view currently holds.
  private _rowObserver?: ResizeObserver;
  private _observedRows = new WeakSet<HTMLElement>();

  lastReadAt = toSignal(this._readState.lastReadAt$, { initialValue: {} });
  atBottom = toSignal(this._atBottom$, { initialValue: true });
  unreadIds = computed(() => {
    const at = this.lastReadAt();
    return new Set(
      this._allMessages()
        .filter((m) => isUnread(m, at))
        .map((m) => m.id)
    );
  });
  // Unread count scoped to the room currently displayed, derived from the
  // already-filtered message list — the in-chat banner must only count
  // messages the user could actually jump to in this room.
  roomUnreadCount = computed(() => this.unreadIds().size);
  chatLayout = toSignal(this._userSettings.chatLayout$, {
    initialValue: 'left' as const,
  });
  // Human-readable room display name for the empty-state placeholder. The
  // ODIN `Room.id` is the server-minted unique id (not user-facing); the
  // display name lives in user settings.
  roomName = toSignal(this._userSettings.roomName$, { initialValue: '' });
  // Stays visible during scroll so the user can grab "Back to latest" mid-
  // gesture; only returning to the bottom (or clearing unreads) hides it.
  // Gated on the room having messages at all: an empty room has nothing to
  // scroll to, so neither overlay button may appear there.
  private _overlayEligible = computed(
    () => this._allMessages().length > 0 && (this.roomUnreadCount() > 0 || !this.atBottom())
  );
  private _showOverlay$ = toObservable(this._overlayEligible).pipe(
    distinctUntilChanged(),
    switchMap((show) => (show ? timer(250).pipe(map(() => true)) : of(false)))
  );
  showOverlay = toSignal(this._showOverlay$, { initialValue: false });

  replyTarget = signal<Message | undefined>(undefined);
  replySnippet = computed(() => {
    const t = this.replyTarget();
    return t ? this._chatService.buildQuotedMessage(t).textSnippet : '';
  });
  // A reply to a private message stays private (see sendMsg). The composer
  // strip reflects that and addresses the DM counterparty — the original
  // receiver when we sent the quoted message, otherwise its sender (whose
  // `name` is the other party for a message we received).
  replyIsPrivate = computed(() => this.replyTarget()?.scope === 'private');
  replyToName = computed(() => {
    const t = this.replyTarget();
    if (!t) return '';
    if (t.scope === 'private') {
      return (t.origin === 'self' ? t.receiver?.name : t.sender.name) ?? t.sender.name;
    }
    return t.sender.name;
  });
  highlightedMessageId = signal<string | undefined>(undefined);
  private _highlightClearTimer?: ReturnType<typeof setTimeout>;

  @ViewChild('sharedEmojiMenu') sharedEmojiMenu?: ContextMenu;
  @ViewChild('sharedEmojiPicker') sharedEmojiPicker?: EmojiPicker;
  private _reactionTarget: Message | null = null;

  constructor(
    public odinService: OdinService,
    public messageStore: MessageStore,
    private _chatService: ChatService,
    private _readState: MessageReadStateStore,
    private _userService: UserService,
    private _userStore: UserStore,
    private _userSettings: UserSettingsStore,
    private _translate: TranslateService,
    private _devConsole: DevConsoleService
  ) {
    // Register so `window.chat.purgeAll()`, `chat.scrollToLatest()` etc. can
    // reach this instance from the browser console / E2E tests. Mirrors
    // testbar button wiring without requiring the testbar to be visible.
    this._devConsole.registerChat(this);
  }

  private _showError(key: string, params?: Record<string, string | number>): void {
    this.errorMessage.set(this._translate.instant(key, params));
    if (this._errorTimer) clearTimeout(this._errorTimer);
    this._errorTimer = setTimeout(() => this.errorMessage.set(undefined), 5000);
  }

  protected _formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  private _readAsDataURL(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve((e.target as FileReader).result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  private async _compressPendingBlob(): Promise<string | null> {
    if (!this._pendingBlob) return null;
    const blob = this._pendingBlob;
    const file = blob instanceof File ? blob : new File([blob], 'image', { type: blob.type });
    // Main thread on purpose: the library's worker importScripts() itself from
    // a CDN at runtime; a one-off compress per sent image is fine without it.
    const compressed = await imageCompression(file, {
      maxSizeMB: Chat.MAX_IMAGE_BYTES / (1024 * 1024),
      maxWidthOrHeight: 3840,
      fileType: 'image/jpeg',
      useWebWorker: false,
    });
    if (compressed.size > Chat.MAX_IMAGE_BYTES) {
      this._showError('Image could not be compressed below [max].', {
        max: this._formatBytes(Chat.MAX_IMAGE_BYTES),
      });
      return null;
    }
    return this._readAsDataURL(compressed);
  }

  ngOnInit(): void {
    combineLatest([this.messageStore.messages$, this._userSettings.roomId$])
      .pipe(
        map(([messages, roomId]) => messages.filter((m) => m.roomId === roomId)),
        takeUntil(this._destroy$)
      )
      .subscribe(async (filtered) => {
        const wasAtBottom = this._atBottom$.value;
        this._allMessages.set(filtered);
        if (!wasAtBottom) return;
        await sleep(100);
        // Skip the scroll if we're still flush with the bottom. messages$
        // also emits for upserts (reactions, OGP embeds) — those don't grow
        // the content but still re-emit. Calling `scrollTo` when already at
        // the bottom still fires a programmatic scroll event that the user
        // perceives as a tiny stutter; only re-anchor when content actually
        // grew enough to drift us off the bottom.
        if (!this.viewport) return;
        const bottomOffset = this.viewport.measureScrollOffset('bottom');
        if (bottomOffset > 1) this.scrollToLatestMsg();
      });

    this._userSettings.roomId$
      .pipe(distinctUntilChanged(), takeUntil(this._destroy$))
      .subscribe(() => {
        // A stale "scrolled up" flag from the previous room would keep the
        // overlay's "Back to latest" visible in a room that can't scroll —
        // an empty one never fires the scroll event that would correct it.
        if (!this._atBottom$.value) this._atBottom$.next(true);
        afterNextRender(() => this.scrollToLatestMsg(), {
          injector: this._injector,
        });
      });

    fromEvent(document, 'visibilitychange')
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => this._tabVisible$.next(document.visibilityState === 'visible'));

    this._tabVisible$
      .pipe(distinctUntilChanged(), takeUntil(this._destroy$))
      .subscribe((visible) => {
        if (visible && this._atBottom$.value) this.scrollToLatestMsg();
      });

    merge(
      this.messageStore.messages$,
      this._atBottom$,
      this._tabVisible$,
      this._userSettings.roomId$
    )
      .pipe(debounceTime(150), takeUntil(this._destroy$))
      .subscribe(() => this._tryAdvanceReadMarker());

    // The strategy's height cache is keyed by message id and would otherwise
    // grow for the lifetime of the session — retention prunes and deletions
    // remove messages but not their cached measurements. Sync it against the
    // full store (all rooms, so revisiting a room keeps its warm heights).
    // Debounced: the store emits on every add/upsert and the prune only
    // matters in bulk.
    this.messageStore.messages$
      .pipe(debounceTime(1000), takeUntil(this._destroy$))
      .subscribe((all) => this._strategy?.pruneHeights(new Set(all.map((m) => m.id))));
  }

  async sendMsg() {
    const text = this.inputField.getContent();

    if (this.inputField.isDisabled() || ((!text || text.length === 0) && !this.imagePreview())) {
      return;
    }

    if (text && text.length > Chat.MAX_TEXT_LENGTH) {
      this._showError('Message too long — [count]/[max] characters. Please shorten it.', {
        count: text.length,
        max: Chat.MAX_TEXT_LENGTH,
      });
      return;
    }

    this.inputField.setDisabled();
    this.sendButton.nativeElement.disabled = true;
    this.sendButton.nativeElement.style.cursor = 'progress';

    const payload: TextMessageWirePayload = {};

    if (text) {
      payload.text = text;
    }

    let imageData = this.imagePreview();
    if (imageData && this.imageNeedsCompression()) {
      this.isCompressing.set(true);
      const compressed = await this._compressPendingBlob();
      if (!compressed) {
        this.isCompressing.set(false);
        this.inputField.setEnabled();
        this.sendButton.nativeElement.disabled = false;
        this.sendButton.nativeElement.style.cursor = 'pointer';
        return;
      }
      imageData = compressed;
    }
    if (imageData) {
      // Read intrinsic dimensions from the FINAL data URL (post-compression).
      // Recipients use these for the `<img width height>` attrs so the row
      // height is correct before image decode finishes. Failure to decode
      // here is non-fatal — the message still ships, just without dims.
      let dims: { width: number; height: number } | undefined;
      try {
        dims = await imageDimensions(imageData);
      } catch {
        // Image failed to decode for size — fall back to undefined dims.
      }
      payload.attachment = {
        type: 'image',
        data: imageData,
        ...(dims ?? {}),
      };
    }

    const target = this.replyTarget();
    if (target) {
      payload.quotedMessage = this._chatService.buildQuotedMessage(target);
    }

    const ownUser = this._userService.ownUser();

    if (target?.scope === 'private') {
      // Replying to a private message stays private: route it back to the DM
      // counterparty. `recipientsForTarget` resolves that user — and thus
      // their current peerId — from the store. If they've left the room there
      // is no one to deliver to; abort with a notice rather than fall back to
      // a room broadcast, which would leak the private thread. The draft and
      // reply target are preserved so the user can retry or cancel.
      const recipients = this._chatService.recipientsForTarget(target);
      if (!recipients || recipients.length === 0) {
        this._showError('Cannot send a private reply — [name] is no longer in the room.', {
          name: (target.origin === 'self' ? target.receiver?.name : target.sender.name) ?? '',
        });
        this.isCompressing.set(false);
        this.inputField.setEnabled();
        this.sendButton.nativeElement.disabled = false;
        this.sendButton.nativeElement.style.cursor = 'pointer';
        return;
      }
      const messageTransfer = {
        id: crypto.randomUUID(),
        scope: 'private' as const,
        payload,
        kind: 'message' as const,
      };
      await this.odinService.sendMessage(messageTransfer, recipients);
    } else {
      const messageTransfer = {
        id: crypto.randomUUID(),
        scope: 'room' as const,
        payload,
        kind: 'message' as const,
      };
      const ownBreakout = ownUser?.data.breakoutRoomId ?? '';

      if (ownBreakout !== '' && ownUser) {
        const peers = (
          Object.values(this._userStore.store.getValue().entities) as (User | undefined)[]
        ).filter((u): u is User => !!u && !u.isSelf && u.data.breakoutRoomId === ownBreakout);

        if (peers.length === 0) {
          await this._chatService.buildMessage(ownUser, messageTransfer);
        } else {
          await this.odinService.sendMessage(messageTransfer, peers);
        }
      } else {
        await this.odinService.sendMessage(messageTransfer, []);
      }
    }

    this.inputField.clear();
    this.imagePreview.set(undefined);
    this.isCompressing.set(false);
    this.imageNeedsCompression.set(false);
    this._pendingBlob = null;
    this.replyTarget.set(undefined);
    this.inputField.setEnabled();
    this.sendButton.nativeElement.disabled = false;
    this.sendButton.nativeElement.style.cursor = 'pointer';

    // Force scroll to the new message even if the user was scrolled up — the
    // auto-follow subscription gates on `_atBottom$.value`, but submitting
    // your own message is an explicit intent to see it land.
    await sleep(150);
    this.scrollToLatestMsg();
  }

  async addAttachment() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';

    input.onchange = async (e: Event) => {
      const input = e.target as HTMLInputElement;
      if (!input.files) return;
      const file = input.files[0];
      this.imagePreview.set(await this._readAsDataURL(file));
      if (file.size > Chat.MAX_IMAGE_BYTES) {
        this._pendingBlob = file;
        this.imageNeedsCompression.set(true);
      }
    };

    input.click();
  }

  paste = async (event: ClipboardEvent) => {
    const items = event.clipboardData?.items || [];
    for (const item of items) {
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        event.preventDefault();
        const blob = item.getAsFile();
        if (!blob) return;
        this.imagePreview.set(await this._readAsDataURL(blob));
        if (blob.size > Chat.MAX_IMAGE_BYTES) {
          this._pendingBlob = blob;
          this.imageNeedsCompression.set(true);
        }
        return;
      }
    }
  };

  removeAttachment() {
    this.imagePreview.set(undefined);
    this.isCompressing.set(false);
    this.imageNeedsCompression.set(false);
    this._pendingBlob = null;
  }

  viewImage = (event: any) => {
    event.preventDefault();
    this.popoverImage.set(event.target.src);
    this.imagePopoverContainer.nativeElement.showModal();
  };

  closeViewImage(event: any, force?: true) {
    event.preventDefault();
    if (event.target === this.imagePopoverContainer.nativeElement || force) {
      this.imagePopoverContainer.nativeElement.close();
    }
  }

  ngAfterViewInit(): void {
    // Defer to next microtask so the viewport's ElementRef is populated and
    // its initial measurement has completed before we attach observers.
    queueMicrotask(() => {
      // Feed the strategy a lookup from index → message id. The strategy uses
      // this to key its height cache by id (stable across renders) rather than
      // by index (would shift if a message was inserted in the middle).
      this._strategy?.setItemIdProvider({
        itemIdAt: (idx) => this._allMessages()[idx]?.id,
      });

      // Per-row height estimator — Phase 2a. Replaces the strategy's
      // static `defaultItemHeight: 80` fallback with a per-message
      // heuristic that mirrors the chat-msg template's height
      // contributors (header, text wrap, attachment dims, embed card,
      // reactions). Cascade adjustment when ResizeObserver lands the real
      // measurement shrinks from "default 80 vs actual N" to "estimate vs
      // actual", typically small enough to be imperceptible. The
      // ResizeObserver refinement pipeline below still feeds exact
      // heights into the strategy as rows paint.
      this._strategy?.setHeightEstimator((idx) => {
        const msg = this._allMessages()[idx];
        if (!msg) return 80;
        // The estimator wraps text against the viewport's *width*. Don't use
        // `getViewportSize()` here — for a vertical viewport that returns the
        // height. Fallback covers a not-yet-laid-out viewport; the viewport
        // ResizeObserver below re-invalidates estimates once layout settles.
        const width = this.viewport.elementRef.nativeElement.clientWidth || 400;
        return estimateMessageHeight(msg, width);
      });

      // Layout / viewport-width changes invalidate the estimates — re-
      // request a prefix-sum rebuild. The strategy's `invalidateEstimates`
      // re-asks the estimator for every unmeasured row's height.
      const resizeObserver = new ResizeObserver(() => {
        this._strategy?.invalidateEstimates();
      });
      resizeObserver.observe(this.viewport.elementRef.nativeElement);
      this._destroy$.subscribe(() => resizeObserver.disconnect());

      this.scrollToLatestMsg();

      // Track atBottom for the auto-follow gate, the "Back to latest" overlay,
      // and the read-marker advance.
      this.viewport
        .elementScrolled()
        .pipe(takeUntil(this._destroy$))
        .subscribe(() => {
          const bottomOffset = this.viewport.measureScrollOffset('bottom');
          const atBottom = bottomOffset < Chat._AT_BOTTOM_TOLERANCE_PX;
          if (this._atBottom$.value !== atBottom) this._atBottom$.next(atBottom);
        });

      // Per-row ResizeObserver: feeds measured heights to the strategy so it
      // can build an exact prefix-sum of offsets. After each rendered range
      // change, walk the visible rows and attach the observer to any we
      // haven't observed before. The observer auto-fires once per element on
      // first observe — that initial fire is what populates the height cache
      // before the strategy needs the value for scroll/offset math. Batched
      // into a single `setHeights` per observer tick because each entry
      // otherwise triggers a full O(N) prefix-sum rebuild.
      //
      // Heights are reported as "contributed" heights (offsetTop diff to the
      // next sibling), not isolated per-row border boxes. Bundled rows apply
      // `margin-top: -0.3rem` to overlap their predecessor; isolated heights
      // would overshoot the wrapper's stacked layout by the cumulative
      // overlap and leave blank space below the last row. The sweep
      // recomputes every rendered row on each tick because a single row's
      // size change can shift its predecessor's contributed value.
      this._rowObserver = new ResizeObserver(() => {
        const strategy = this._strategy;
        if (!strategy) return;
        const wrapper = this.viewport.elementRef.nativeElement.querySelector<HTMLElement>(
          '.cdk-virtual-scroll-content-wrapper'
        );
        if (!wrapper) return;
        const rows = wrapper.querySelectorAll<HTMLElement>('.message-row');
        const updates: Array<readonly [string, number]> = [];
        for (let i = 0; i < rows.length; i++) {
          const el = rows[i];
          if (!el.id.startsWith('msg-')) continue;
          const id = el.id.slice(4);
          const next = rows[i + 1];
          const h = next ? next.offsetTop - el.offsetTop : el.offsetHeight;
          if (h > 0) updates.push([id, h]);
        }
        if (updates.length > 0) strategy.setHeights(updates);
      });

      this.viewport.renderedRangeStream.pipe(takeUntil(this._destroy$)).subscribe(() => {
        // Defer to next frame so Angular has finished rendering the new range.
        requestAnimationFrame(() => this._observeRenderedRows());
      });

      // Cover the cold-start case: by the time we subscribe above, the
      // strategy may have already emitted its initial rendered range (e.g.
      // when messages were rehydrated from IDB before `ngAfterViewInit`).
      // Without this kick the heights cache stays empty until the user
      // scrolls, and `totalContentSize` falls back to estimator sums.
      requestAnimationFrame(() => this._observeRenderedRows());
    });
  }

  private _observeRenderedRows(): void {
    if (!this._rowObserver) return;
    const root = this.viewport.elementRef.nativeElement as HTMLElement;
    const rows = root.querySelectorAll<HTMLElement>('.message-row');
    for (const row of Array.from(rows)) {
      if (!this._observedRows.has(row)) {
        this._observedRows.add(row);
        this._rowObserver.observe(row);
      }
    }
  }

  ngOnDestroy(): void {
    this._devConsole.unregisterChat(this);
    this._rowObserver?.disconnect();
    this._destroy$.next();
    this._destroy$.complete();
  }

  // Auto-follow path (new messages, tab visibility, room change) and the
  // "Back to latest" overlay both land here. Single `scrollTo` is enough
  // now that the strategy's per-row height estimator gets total content
  // size accurate to within ~76 px (p95) on first paint — the prior
  // iterative retry existed because the static-80 default could leave
  // hundreds of px on the table. The strategy's ResizeObserver feedback
  // and anchor preservation close any small residual gap on the next
  // frame, with no further intervention here.
  scrollToLatestMsg(): void {
    if (!this.viewport) return;
    this.viewport.scrollTo({ bottom: 0 });
  }

  scrollToLatest(): void {
    this.scrollToLatestMsg();
  }

  // Strategy supports real `scrollToIndex`, so we can land precisely on a
  // target message in one call. `scrollIntoView` afterwards refines the
  // alignment ('start' / 'center') and only fires once the row is in the
  // rendered range (which is guaranteed by `scrollToIndex`).
  private _scrollToMessageId(id: string, block: ScrollLogicalPosition): void {
    const messages = this._allMessages();
    const idx = messages.findIndex((m) => m.id === id);
    if (idx === -1) return;
    this.viewport.scrollToIndex(idx, 'auto');
    requestAnimationFrame(() => {
      const el = document.getElementById('msg-' + id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block });
    });
  }

  scrollToFirstUnread(): void {
    const state = this.messageStore.store.getValue();
    const lastReadAt = this._readState.value;
    const currentRoomId = this._userSettings.value.roomId;
    for (const id of state.ids) {
      const m = state.entities[id as string];
      if (m && m.roomId === currentRoomId && isUnread(m, lastReadAt)) {
        this._scrollToMessageId(m.id, 'start');
        break;
      }
    }
    if (currentRoomId) {
      this._readState.markAllAsRead([currentRoomId]);
    }
  }

  scrollToMessage(id: string): void {
    this._scrollToMessageId(id, 'center');
    this.highlightedMessageId.set(id);
    if (this._highlightClearTimer) clearTimeout(this._highlightClearTimer);
    this._highlightClearTimer = setTimeout(() => this.highlightedMessageId.set(undefined), 1500);
  }

  setReplyTarget(target: Message | undefined): void {
    this.replyTarget.set(target);
  }

  toggleReaction(target: Message, emoji: string): void {
    this._chatService.sendReaction(target, emoji).catch((e) => console.error(e));
  }

  onReactRequested(event: {
    message: Message;
    anchor: HTMLElement;
    xPosition: 'before' | 'after';
  }): void {
    this._reactionTarget = event.message;
    this.sharedEmojiMenu?.open(event.anchor, { xPosition: event.xPosition });
  }

  onSharedEmojiSelected(emoji: string): void {
    if (!emoji) return;
    const target = this._reactionTarget;
    if (!target) return;
    this.toggleReaction(target, emoji);
    this.sharedEmojiMenu?.close();
  }

  onSharedMenuClosed(): void {
    this._reactionTarget = null;
    this.sharedEmojiPicker?.reset();
  }

  jumpToQuotedMessage(id: string): void {
    if (!this.messageStore.has(id)) return;
    this.scrollToMessage(id);
  }

  trackById = (_index: number, message: Message): string => message.id;

  toggleReadState(): void {
    if (this.roomUnreadCount() > 0) {
      this._readState.markAllAsRead(this.messageStore.knownRoomIds());
    } else {
      this._readState.markAllAsUnread();
    }
  }

  // Wipes every message across every room. Useful when re-running the stress
  // fixture — new drafts would otherwise get lost in the pile. The clear
  // propagates to `_allMessages` via the `messages$` subscription.
  purgeAllMessages(): void {
    this.messageStore.clear();
    // Strategy caches heights by id; the new ids may collide with the old
    // (e.g., re-running the stress fixture), so wipe the cache too.
    this._strategy?.clearHeights();
  }

  private _tryAdvanceReadMarker(): void {
    if (!this._atBottom$.value) return;
    if (!this._tabVisible$.value) return;

    const currentRoomId = this._userSettings.value.roomId;
    if (!currentRoomId) return;

    const state = this.messageStore.store.getValue();
    let latestTime = 0;
    for (const id of state.ids) {
      const m = state.entities[id as string];
      if (!m) continue;
      if (m.origin === 'self') continue;
      if (m.roomId !== currentRoomId) continue;
      const t = m.time.getTime();
      if (t > latestTime) latestTime = t;
    }
    if (latestTime > 0) {
      this._readState.markReadUpTo(currentRoomId, latestTime);
    }
  }
}
